import React from 'react';
import { Alert } from 'antd';

export interface HtmlDeliverableFrameProps {
  /** Presigned, inline-served (`text/html`) URL for the HTML deliverable. */
  downloadUrl?: string;
  /** Deliverable title — used as the iframe's accessible name. */
  title: string;
}

/**
 * Renders an HTML deliverable inline via Mechanism B (ADR-0003 Decision 6): a
 * sandboxed `<iframe src>` pointed at the presigned, inline-served URL. The sandbox is
 * `allow-scripts allow-downloads` with **no** `allow-same-origin` (opaque origin) — the
 * spike proved this renders script/Chart.js/font-driven deliverables and supports
 * in-frame downloads. A null URL shows an inline error rather than a blank iframe.
 * @param {HtmlDeliverableFrameProps} props - The presigned URL and title.
 * @returns {React.ReactElement} The sandboxed iframe, or an inline error state.
 */
export function HtmlDeliverableFrame({ downloadUrl, title }: HtmlDeliverableFrameProps): React.ReactElement {
  if (!downloadUrl) {
    return (
      <div className="h-full flex items-center justify-center p-6">
        <Alert
          type="error"
          showIcon
          message="Unable to open deliverable"
          description="No download URL is available for this HTML deliverable."
        />
      </div>
    );
  }

  return (
    <iframe
      src={downloadUrl}
      title={title}
      sandbox="allow-scripts allow-downloads"
      className="w-full h-full border-0"
    />
  );
}
